import { Injectable, inject, signal } from '@angular/core';
import { ActivityEntityName, CodeStatus, EntityStatusType, PickerEntityStatusRequest, StatusPickerOption, tryCatch } from '@jms/model';
import { EntityDataServiceRaw } from '@workbuddy/services/entities';
import { firstValueFrom } from 'rxjs';

/** The two sets a line's status comes from: Items (Pending) on a pending line, Items on an authorised one. */
type ItemStatusSetType = ActivityEntityName.ItemPending | ActivityEntityName.Item;

/**
 * The Items (Pending) and Items sets, for gates the row itself cannot answer.
 *
 * The stamp on a line carries no `isDefault` and no `editableBy`, so `canApproveInStatus` read off a row
 * says yes to Draft, and nothing on the row says whether its status locks it. The set is the only place
 * those answers exist.
 */
@Injectable({ providedIn: 'root' })
export class ItemStatusSetService {

  private readonly entityData = inject(EntityDataServiceRaw);

  private readonly pending = new Map<ItemStatusSetType, Promise<StatusPickerOption[]>>();
  // A signal, so a row menu built before a set arrives re-reads it when it does: the board builds a row's
  // commands once, when the row first renders, which is often before the sets come back.
  private readonly $resolved = signal<Partial<Record<ItemStatusSetType, StatusPickerOption[]>>>({});

  /** Cached per app, not per board: a status set changes in Settings, which reloads the app. */
  public load(): Promise<StatusPickerOption[][]> {

    return Promise.all([ActivityEntityName.ItemPending, ActivityEntityName.Item].map(entityType => this.loadSet(entityType as ItemStatusSetType)));
  }

  /** The status a cancel lands on, so its dialog can ask only for the reason. Null leaves the dialog showing its picker. */
  public async terminalStatus(entityType: ActivityEntityName): Promise<StatusPickerOption | null> {

    // Resolved by `systemName` because a tenant renames its statuses, and the server picks the same one.
    const systemName = entityType === ActivityEntityName.ItemPending ? CodeStatus.Declined : CodeStatus.Cancelled;
    const options = await this.fetch({ entityType, displayType: EntityStatusType.Inactive, restrictToSelectable: true });

    return options.find(option => option.systemName === systemName) ?? null;
  }

  /** What is in hand right now; read inside a `computed`, it follows the set as it arrives. */
  public snapshot(entityType: ItemStatusSetType = ActivityEntityName.ItemPending): StatusPickerOption[] | null {

    return this.$resolved()[entityType] ?? null;
  }

  private loadSet(entityType: ItemStatusSetType): Promise<StatusPickerOption[]> {

    // No displayType and no restrictToSelectable: a gate has to see Draft and the statuses Office cannot pick, which no picker offers.
    let request = this.pending.get(entityType);
    if (!request) {
      request = this.fetch({ entityType });
      this.pending.set(entityType, request);
    }

    return request;
  }

  private async fetch(request: PickerEntityStatusRequest): Promise<StatusPickerOption[]> {

    const entityType = request.entityType as ItemStatusSetType;
    const wholeSet = !request.displayType && !request.restrictToSelectable;
    const [error, response] = await tryCatch(firstValueFrom(this.entityData.getPicker(ActivityEntityName.EntityStatus, request)));

    // A failed read must not cache: the next row rebuilding its menu should try again.
    if (error) {
      if (wholeSet) this.pending.delete(entityType);
      return [];
    }

    const items = (response?.items as StatusPickerOption[]) ?? [];
    // Only the whole set is cached for `snapshot`; a filtered fetch answers its caller and nothing else.
    if (wholeSet) this.$resolved.update(sets => ({ ...sets, [entityType]: items }));

    return items;
  }
}
